import { z } from 'zod';
/**
 * The worker's scheduled loops and what "healthy" means for each (`.claude/rules/scheduled-jobs.md`).
 * Every loop upserts a `job_heartbeats` row from a `finally` on every tick; the watcher
 * (`GET /admin/jobs/heartbeats`) compares it to these declarations.
 *
 * Two different facts, kept apart on purpose:
 *  - `expectedEverySeconds` — how often the loop must RUN. Missing that is an outage.
 *  - `idleIsHealthy` — whether "ran but had nothing to do" is fine. For a queue drainer it is:
 *    an empty queue is the normal state, and alarming on it is how a watcher gets muted.
 */
export const WORKER_JOBS = {
    'event-outbox-drain': {
        description: 'Publishes event_outbox rows to SQS',
        expectedEverySeconds: 5,
        idleIsHealthy: true,
    },
    'audit-partitions': {
        description: 'Keeps audit.log partitions two months ahead',
        expectedEverySeconds: 24 * 60 * 60,
        idleIsHealthy: true,
    },
    'duplicate-report': {
        description: 'Nightly report of candidates sharing an email or phone (runs at 02:00 New York)',
        // Checked every 15 minutes, building the report once a night: the heartbeat proves the
        // loop is alive between nights.
        expectedEverySeconds: 15 * 60,
        idleIsHealthy: true,
    },
    'webhook-delivery': {
        description: 'Fans outbox events out to webhook subscriptions and POSTs due deliveries',
        expectedEverySeconds: 5,
        idleIsHealthy: true,
    },
    'resume-text-extraction': {
        description: 'Extracts PDF/DOCX resume text into candidates.resume_text for search',
        expectedEverySeconds: 10,
        idleIsHealthy: true,
    },
    'trigger-runs': {
        description: 'Applies due trigger runs; HTTP ones go to the integration-triggers queue',
        expectedEverySeconds: 5,
        // No due run is the normal state; the flag being off is reported as a skip, not idle.
        idleIsHealthy: true,
    },
    'rcrm-webhook-inbox': {
        description: 'Applies stored RecruitCRM webhooks (linked custom fields), retrying failures for 24 hours',
        expectedEverySeconds: 5,
        idleIsHealthy: true,
    },
    'calendly-meetings': {
        description: "Reads the Account Managers' upcoming Calendly meetings from BigQuery into Redis for the Calendar on Home and Team Tasks",
        expectedEverySeconds: 5 * 60,
        // Skipped (still a heartbeat) where GCP_WIF_CONFIG or REDIS_URL is unset.
        idleIsHealthy: true,
    },
    // Not worker loops: Message Hub's Supabase job posts every minute (TS-1892), and the API writes
    // the heartbeat on every request past the auth check, an empty batch included. `optional`: an
    // environment where the feed was never set up has no row, and one whose feed was switched off
    // (`stopped`) reads `off`; neither is an outage. Once it runs, three silent minutes are `stale`.
    // `perOrganisation`: the feed writes into its API key's organisation, so each organisation has
    // its own heartbeat row (`heartbeatKey`) and sees only its own.
    'message-feed-texts': {
        description: "Message Hub's texts arriving from its Supabase job (TS-1892)",
        expectedEverySeconds: 60,
        idleIsHealthy: true,
        optional: true,
        perOrganisation: true,
    },
    'message-feed-emails': {
        description: "Message Hub's client emails arriving from its Supabase job (TS-1892)",
        expectedEverySeconds: 60,
        idleIsHealthy: true,
        optional: true,
        perOrganisation: true,
    },
};
export const WORKER_JOB_NAMES = Object.keys(WORKER_JOBS);
/** `off`: an optional job its source switched off (its last run said `stopped`). */
export const HEARTBEAT_HEALTH = ['ok', 'never_ran', 'stale', 'failing', 'off'];
export const heartbeatHealthSchema = z.enum(HEARTBEAT_HEALTH);
/** Whether a job is optional (see `WORKER_JOBS`): it may have no heartbeat, or be switched off. */
export function jobIsOptional(job) {
    const spec = WORKER_JOBS[job];
    return 'optional' in spec && spec.optional;
}
/**
 * The `job_heartbeats` key of a job: its name, or `<name>:<organisation id>` for a job each
 * organisation runs on its own (the message feed), so one organisation's runs never stand in for
 * another's.
 */
export function heartbeatKey(job, organisationId) {
    const spec = WORKER_JOBS[job];
    return 'perOrganisation' in spec && spec.perOrganisation ? `${job}:${organisationId}` : job;
}
/** Whether a job counts as healthy: `ok`, or an optional job that never ran or was switched off. */
export function jobIsHealthy(view) {
    return (view.health === 'ok' ||
        (view.optional && (view.health === 'never_ran' || view.health === 'off')));
}
/**
 * A loop is `stale` when it has not run for three of its intervals (at least two minutes, so a
 * deploy or a slow tick does not page anyone), `failing` when its latest run threw, and
 * `never_ran` when there is no row at all — which after a deploy means it never started.
 */
export function heartbeatHealth(job, hb, now) {
    if (!hb)
        return 'never_ran';
    if (hb.lastStatus === 'stopped' && jobIsOptional(job))
        return 'off';
    const allowedMs = Math.max(3 * WORKER_JOBS[job].expectedEverySeconds, 120) * 1000;
    if (now.getTime() - hb.lastRunAt.getTime() > allowedMs)
        return 'stale';
    if (hb.lastStatus === 'failed')
        return 'failing';
    return 'ok';
}
//# sourceMappingURL=scheduled-jobs.js.map